import { useEffect } from "react";
import { formatMoney } from "./money";
import { useLocation, useNavigate } from "react-router";
import { BottomCTA, IconBrick, IconXmarkLine, TopNavigation } from "@hamlak/ui";
import { t } from "@/i18n/ko";
import { storeKey, storeOf } from "@/i18n/codes";
import { formatDateTimeDot } from "@/lib/formatDate";
import { platform } from "@/platform";
import { useUiStore } from "@/stores/uiStore";
import type { PurchaseDoneState } from "./usePurchase";
import "./store.css";

const isDoneState = (s: unknown): s is PurchaseDoneState =>
  typeof s === "object" && s !== null && "purchase" in s && typeof (s as PurchaseDoneState).purchase?.purchase?.productId === "string";

/**
 * F06-04 결제 완료(1.15). 라우터 state{purchase, name}이 없으면 /store로. 브릭 히어로 · 영수증 4행 · CTA(닫기 X 동일) → 상점 진입 전 화면(storeReturnTo) 또는 /home, 보조 = 상점 계속 보기.
 */
export function DonePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as unknown;
  const storeReturnTo = useUiStore((s) => s.storeReturnTo);
  const setStoreReturnTo = useUiStore((s) => s.setStoreReturnTo);
  const ok = isDoneState(state);

  useEffect(() => {
    if (!ok) navigate("/store", { replace: true });
  }, [ok, navigate]);

  if (!ok) return null;
  const { purchase, name } = state;
  const p = purchase.purchase;
  const finish = () => {
    const to = storeReturnTo ?? "/home";
    setStoreReturnTo(null);
    navigate(to, { replace: true });
  };
  const toChat = storeReturnTo?.startsWith("/chat") ?? false;
  /** 상점으로 되돌아가되 storeReturnTo 는 유지(상점에서 다시 나갈 곳) */
  const keepShopping = () => navigate("/store", { replace: true });

  const charged = purchase.status !== "already_granted";
  const rows: Array<[string, string]> = [
    // 상품 = "브릭 {N}개"(상품 별칭 없음, AC0-263)
    [t("store.done.product"), name],
    [t("store.done.price"), p.currency && p.amountMicros !== null ? formatMoney({ currency: p.currency, amountMicros: p.amountMicros }) : t("store.price", { price: p.priceKrw })],
    [t("store.done.method"), t(storeKey("store.done.methodValue", storeOf(platform.clientKind)))],
    [t("store.done.at"), formatDateTimeDot(p.purchasedAt)],
  ];

  return (
    <main className="page done-page" data-testid="store-done">
      {/* 시안 F06-04 43:834: 제목 없음 · 오른쪽 닫기 X, 브릭 히어로 → 제목 t9 → 부제 2줄 t5 → 영수증 카드 → CTA 2개 */}
      <TopNavigation
        title=""
        left={null}
        right={
          <button type="button" className="done__close" aria-label={t("common.close")} onClick={finish}>
            <IconXmarkLine size={24} />
          </button>
        }
      />
      <div className="done">
        <span className="done__hero">
          <IconBrick size={64} />
        </span>
        <p className="done__title">{charged ? t("store.done.charged", { bricks: p.bricks }) : t("store.done.title")}</p>
        <p className="done__sub">
          {charged ? t("store.done.sub") : t("store.done.alreadyGranted")}
          <br />
          {t("store.done.balance", { balance: purchase.balance })}
        </p>
        <dl className="done__receipt">
          {rows.map(([k, v]) => (
            <div key={k} className="done__row">
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <BottomCTA
        primary={{ children: toChat ? t("store.done.ctaChat") : t("store.done.cta"), onClick: finish }}
        secondary={{ children: t("store.done.continue"), onClick: keepShopping }}
      />
    </main>
  );
}
